/**
 * DuplicateMatchDialog — popup of patient records that potentially
 * duplicate the form being filled.
 *
 * Mirrors Flutter `patient_selection_widget.dart` + `view_patient_popup.dart`.
 * The Flutter widget composes a 3-column table (Name / ID Type / ID
 * Number) with single-selection radio behavior, opens a slide-out
 * detail when a row is tapped, and has two footer buttons:
 *   - "Use Selected Record" (loads that patient — abandons the new
 *     creation)
 *   - "Create New Record Anyway" (`UseRecordAnywayEvent` — clears
 *     matching state, re-enables Save)
 *
 * Per CLAUDE.md "Flutter-web workaround simplification": we render the
 * row detail inline rather than a nested slide-out popup (shadcn Dialog
 * + table → simpler React idiom, same information density). The
 * detail-view popup itself ports under PROD-726 follow-up if needed.
 *
 * Two action callbacks:
 *   - `onSelectExisting(match)` — caller decides what "use this record"
 *     means for its source. Patient-identity callers navigate to
 *     `/patients/edit/<id>`. Guardian callers can choose to auto-fill
 *     the guardian fields (out of scope this PR — file PROD-7xx).
 *     `null` means the "Use" affordance is hidden.
 *   - `onUseAnyway()` — user wants to ignore the matches and continue
 *     with the new record. Caller toggles the suppression flag in its
 *     local state; the matching-records save-gate will read it.
 */

import { useNavigate } from 'react-router-dom';
// GAP (SYSTEM-RULES §3): no Signal DS glyph for "add a person / new record" (`UserPlus`); it stays
// lucide in both looks.
import { Eye, UserPlus } from 'lucide-react';
import {
  Button,
  Modal,
  CustomTableList,
  type CustomTableColumn,
} from '@scripsteam/scrips-signal-ds';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import type { MatchingPatient } from '@/types/patient-api';

export type DuplicateMatchSource = 'patient-identity' | 'guardian' | 'contact';

export interface DuplicateMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matches: MatchingPatient[];
  /** Which kind of match this popup is reviewing — drives the title +
   *  description copy. */
  source: DuplicateMatchSource;
  /** Optional per-row "Use this record" action. When provided, renders
   *  a button on each row. Patient-identity callers typically navigate
   *  to the existing patient's edit page; guardian callers may auto-fill
   *  the guardian fields (deferred to follow-up). */
  onSelectExisting?: (match: MatchingPatient) => void;
  /** "Continue with new record" footer action. Suppresses the
   *  matching-records save-gate in the caller's state. Optional —
   *  defensively called with `?.()` so a forgotten prop doesn't throw
   *  at runtime; the TS signature still flags the omission. */
  onUseAnyway?: () => void;
  /** Per-row "View" action. Defaults to navigating to
   *  `/patients/edit/<id>`. Stories override to log into the Actions
   *  panel so the navigate intent is visible during manual QA. */
  onView?: (match: MatchingPatient) => void;
}

function copyForSource(source: DuplicateMatchSource, count: number) {
  const noun = count === 1 ? 'record' : 'records';
  switch (source) {
    case 'patient-identity':
      return {
        title: count === 1
          ? '1 patient with matching ID found'
          : `${count} patients with matching ID found`,
        description: `Selecting an existing ${noun} will abandon the new patient you started creating.`,
      };
    case 'guardian':
      return {
        title: count === 1
          ? '1 patient matches the guardian identity'
          : `${count} patients match the guardian identity`,
        description: `The guardian you entered already exists in this organization. Review the matches before saving.`,
      };
    case 'contact':
      return {
        title: count === 1
          ? '1 patient with matching phone / email'
          : `${count} patients with matching phone / email`,
        description: `Selecting an existing ${noun} will abandon the new patient you started creating.`,
      };
  }
}

export function DuplicateMatchDialog({
  open,
  onOpenChange,
  matches,
  source,
  onSelectExisting,
  onUseAnyway,
  onView,
}: DuplicateMatchDialogProps) {
  // PROD-2743 · switch ON: the DS `eye` glyph on View (16px inline). OFF = today.
  const navigate = useNavigate();
  const count = matches.length;
  const copy = copyForSource(source, count);

  const handleView = (match: MatchingPatient) => {
    if (!match.patientId) return;
    onOpenChange(false);
    if (onView) {
      onView(match);
      return;
    }
    navigate(`/patients/edit/${match.patientId}`);
  };

  const columns: CustomTableColumn<MatchingPatient>[] = [
    {
      id: 'patient',
      header: 'Patient',
      cell: (m) => (
        <div className="flex items-center gap-2 min-w-0">
          <Avatar
            scale="compact"
            tone="patient"
            name={fullNameOf(m)}
            src={m.profileImageURL ?? undefined}
            size="sm"
          />
          <span className="text-sm font-medium truncate">{fullNameOf(m)}</span>
        </div>
      ),
    },
    {
      id: 'idNumber',
      header: 'ID Number',
      cell: (m) => (
        <span className="text-sm font-mono text-text-muted">
          {idNumberOf(m) ?? '—'}
        </span>
      ),
    },
    {
      id: 'dob',
      header: 'DOB',
      cell: (m) => (
        <span className="text-sm text-text-muted">{formatDate(m.birthDate)}</span>
      ),
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: (m) => (
        <span className="text-sm text-text-muted">
          {m.contactNumber ?? m.email ?? '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      cell: (m) => (
        <div className="inline-flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="tertiary"
            onClick={() => handleView(m)}
            disabled={!m.patientId}
          >
            <Eye className="size-4 mr-1" />
            View
          </Button>
          {onSelectExisting ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                onSelectExisting(m);
                onOpenChange(false);
              }}
            >
              Use
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={copy.title}
      description={copy.description}
      size="xl"
      footer={<><Button
            type="button"
            variant="secondary"
            onClick={() => {
              // Defensive call — onUseAnyway is optional on the type so
              // a forgotten prop doesn't throw at runtime (Samer review
              // on PR #57).
              onUseAnyway?.();
              onOpenChange(false);
            }}
          >
            <UserPlus className="size-4 mr-1" />
            Continue with new record
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button></>}
      footerAlign="between"
    >
      <CustomTableList
        data={matches}
        columns={columns}
        caption={copy.title}
        emptyMessage="No matching records"
      />
    </Modal>
  );
}

/** Identity lives under `identification`; top-level idNumber is a legacy
 *  null on the Validate row. */
function idNumberOf(m: MatchingPatient): string | null | undefined {
  return m.identification?.idNumber ?? m.idNumber;
}

function fullNameOf(m: MatchingPatient): string {
  return (
    m.name ||
    [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ') ||
    '—'
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
